<template>
    <div id="app" class="bg-backdrop">
        <splash v-if="!loaded" class="h-screen" :progress="loadingProgress"/>

        <language v-else-if="!userLanguage"/>

        <onboarding
                v-else-if="!seenTutorials && onboardingAvailable"
                @finished="finishedOnboarding"
        />

        <tutorials
                v-else-if="!seenTutorials"
                @finished="finishedTutorial"
        />

        <router-view v-else class="min-h-screen"/>

        <template v-if="loaded">
            <f-dialog
                    ref="gameUnlockedNoticeRef"
                    v-if="!seenGameUnlockedNotice"
                    :title="$t('trend.predictor.unlock.title')"
                    :message="$t('trend.predictor.unlock.description')"
                    :okLabel="$t('trend.predictor.unlock.button.ok')"
                    :cancelLabel="$t('trend.predictor.unlock.button.cancel')"
                    :content-classes="['text-white', 'bg-positive/90']"
            />

            <upgrade-sheet ref="upgradeSheetRef"/>

            <feedback-modal ref="feedbackModalRef"/>
        </template>

        <promo ref="promoRef"/>

        <portal-target name="modals" multiple/>

        <portal-target name="fullscreen-modals" multiple/>
    </div>
</template>

<script setup>
import GameService, { GAME_THRESHOLD } from '@/services/GameService'
import SoundService from '@/services/SoundService'
import Splash from '@/modules/core/Splash.vue'
import Language from '@/modules/core/Language.vue'
import Tutorials from '@/modules/core/Tutorials.vue'
import { Scores, Settings, User } from '@/store'
import { Network } from '@capacitor/network'
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update'
import { Capacitor } from '@capacitor/core'
import SubService from '@/services/SubService'
import gameUnlockedSound from '@/assets/sounds/game_unlocked.mp3'
import AppService from '@/services/AppService'
import UpgradeSheet from '@/components/subscriptions/UpgradeSheet.vue'
import { Device } from '@capacitor/device'
import Crashlytics from '@/services/Crashlytics'
import Promo from '@/components/Promo.vue'
import { getPromo, clearPromo } from '@/services/PromoService'
import networkService from '@/services/NetworkService'
import ConfigService from '@/services/ConfigService'
import StorageService from '@/services/StorageService.js'
import FeedbackModal from '@/components/FeedbackModal.vue'
import SchoolService from '@/services/SchoolService.js'
import { countAppSessions, useAppStartUp } from '@/services/AppBoot.js'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToasted } from '@hoppscotch/vue-toasted'
import { useProgressManager } from '@/services/ProgressManager.js'
import Onboarding from '@/modules/core/onboarding/Onboarding.vue'
import AuthService from '@/services/AuthService.js'
import ApiService from '@/services/ApiService.js'
import OnboardingService from '@/services/OnboardingService.js'

const router = useRouter()
const { locale } = useI18n()
const toasted = useToasted()
const progressManager = useProgressManager()
const { startup } = useAppStartUp()

const loaded = ref(false)
const seenGameUnlockedNotice = ref(Settings.getParam('seenGameUnlockedNotice', false))

const gameUnlockedNoticeRef = ref(null)
const promoRef = ref(null)
const upgradeSheetRef = ref(null)
const feedbackModalRef = ref(null)
const onboardingAvailable = ref(false)

const balance = computed(() => Scores.getBalance())
const userLanguage = computed(() => Settings.getLanguage())
const notificationsPermission = computed(() => Settings.getNotifications())
const plan = computed(() => SubService.state.plan)
const loadingProgress = computed(() => progressManager.progress)
const promo = computed(() => getPromo())

const seenTutorials = computed(() => {
    return Settings.getParam('seenTutorials', false) ||
      (AuthService.isAuth() && (!User.getUser().is_new_user || User.getUser().onboarding))
})

// Watch balance
watch(balance, (newVal, oldVal) => {
  if (
    GameService.isLocked(oldVal, plan.value) &&
    newVal >= GAME_THRESHOLD &&
    !Settings.getParam('seenGameUnlockedNotice', false)
  ) {
    SoundService.loadAndPlay('trendPredictor.gameUnlocked', gameUnlockedSound)
    gameUnlockedNoticeRef.value?.open()
      .onOk(() => router.push({ name: 'game' }))
    Settings.setParam('seenGameUnlockedNotice', true)
  }
})

// Watch userLanguage and notificationsPermission to sync with backend
watch([userLanguage, notificationsPermission],
  () => AppService.syncAppInstanceInfo())

// Watch promo
watch(promo, (val) => {
  if (val?.url) {
    promoRef.value?.open(val)
  } else {
    clearPromo()
  }
})

// Watch i18n locale
watch(locale, (newLocale) => {
  const dir = newLocale === 'ar' ? 'rtl' : 'ltr'
  document.documentElement.setAttribute('dir', dir)
})

async function finishedOnboarding () {
  if (AuthService.isAuth()) {
    // Authenticated users will have their onboarding status updated
    await ApiService.updateUserSettings(AuthService.getAuthUser().id, { onboarding: true })
  }

  // Offline device setting that will bypass the onboarding in the future
  // This will propagate to user "onboarding" boolean when user registers
  await Settings.setParam('seenTutorials', true)

  // Register the event for analytics
  AppService.registerAppEvent('onboarding_finished')
}

async function finishedTutorial ({ showPro }) {
  if (AuthService.isAuth()) {
    // Authenticated users will have their onboarding status updated
    await ApiService.updateUserSettings(AuthService.getAuthUser().id, { onboarding: true })
  }

  // Offline device setting that will bypass the onboarding in the future
  // This will propagate to user "onboarding" boolean when user registers
  await Settings.setParam('seenTutorials', true)

  // Register the event for analytics
  AppService.registerAppEvent('onboarding_finished')

  if (showPro) {
    upgradeSheetRef.value.open()
  }
}

async function maybeShowFeedbackRequest () {
  let [
    sessionCount,
    feedbackOnLaunch
  ] = await Promise.all([
    StorageService.get('session_count'),
    ConfigService.getNumber('feedback_on_launch')
  ])

  if (sessionCount > 0 && sessionCount === feedbackOnLaunch) {
    setTimeout(() => feedbackModalRef.value?.open(true), 1500)
  }
}

async function checkBrowser () {
  const {
    webViewVersion,
    platform
  } = await Device.getInfo()
  const version = webViewVersion?.split('.').shift()

  if (platform === 'android' && version < 60) {
    toasted.show('Please update your webview component.', {
      duration: 0,
      action: [
        {
          text: 'Close',
          onClick: (e, toastObject) => toastObject.goAway(0)
        }
      ]
    })
  }
}

async function checkAppUpdate () {
  const result = await AppUpdate.getAppUpdateInfo()

  if (result.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
    toasted.show('New update available!', {
      duration: 0,
      action: [{
          text: 'Update now',
          onClick: (e, toastObject) => {
            toastObject.goAway(0)
            AppUpdate.openAppStore()
          }
      }]
    })
  }
}

onMounted(async () => {
  const { connected } = await Network.getStatus()

  // Check for outdated browsers on Android
  await checkBrowser()

  try {
    await networkService.ensureNetwork(async () => {
      try {
        await startup()

        onboardingAvailable.value = await OnboardingService.onboardingEnabled()

        // wait for 500ms for progress animation to complete
        await new Promise(resolve => setTimeout(resolve, 800))

        loaded.value = true

        // Maybe show feedback request based on session count and feedback trigger setting
        await maybeShowFeedbackRequest()

        // Check for update and show notice
        if (connected && Capacitor.isNativePlatform()) {
          try {
            await checkAppUpdate()
          } catch (error) {
            // Ignore error on offline distributions
          }
        }
      } catch (e) {
        await Crashlytics.recordExceptionWithStacktrace('Failed to initialise' + (e.message ? ': ' + e.message : ''), e)
        const { connected } = await Network.getStatus()

        // If failed to start and connected, show retry option
        if (connected) {
          toasted.show(e.message ?? 'Failed to start.', {
            duration: 0,
            action: {
              text: 'Retry',
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
                startup()
              }
            }
          })
        }
      }
    })
  } catch (e) {
    console.warn('Failed to initialize app', e)
  }

  if (connected) {
    AppService.registerAppEvent('app_launch')
    await SchoolService.addSchoolStylesheet()
  }

  await countAppSessions()
})

onBeforeUnmount(() => {
  SoundService.remove('trendPredictor.gameUnlocked')
})
</script>

<style lang="scss">
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>
